import React, { FC, useEffect, useState } from "react";
import { EventMarkerItem, EventMarkerItemGrid } from "./EventMarker.d";
import { EventMarkerDisplayDocs } from "./ReactChildEventMarkerTable/eventMarkerDisplayDocuments";
import { EditDeletIcons } from "./ReactChildEventMarkerTable/editDeleteIcons";
import { EventMarkerAcronym } from "./ReactChildEventMarkerTable/eventMarkerAcronym";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";

import {
  DARK_GREY,
  INPUT_BACKGROUND,
  PRIMARY_BLUE,
} from "@trane/trane-components/dist/color";
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  gridClasses,
  GridValueGetterParams,
  GridComparatorFn,
} from "@mui/x-data-grid";
import { createTheme, styled, ThemeProvider } from "@mui/material/styles";
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputAdornment,
  OutlinedInput,
  Typography,
} from "@mui/material";
import CustomPagination from "../Dashboard2/CustomPagination";
import {
  ButtonSize,
  ButtonTheme,
} from "@trane/trane-components/dist/simple/button";
import { useDebounce } from "use-debounce";

export interface EventMarkerTableProps {
  rows: Array<EventMarkerItem>;
  labels: any;
  editItem: Function;
  deleteItem: Function;
  showMore: Function;
  downloadAttachment: Function;
  createEventMarker: Function;
}

export const ButtonStyles = styled(Button)({
  background: `${ButtonTheme.primary.light}`,
  borderRadius: "24px",
  height: `${ButtonSize["default"].height}`,
  minWidth: `${ButtonSize["large"].width}`,
  fontFamily: "arial",
  width: "auto",
  color: `${ButtonTheme["primary"]["light"]["enabled"].color}`,
  border: `1px solid ${ButtonTheme["primary"]["light"]["enabled"].border}`,
  fontSize: `${ButtonSize["large"].fontSize}`,
  boxShadow: "rgba(0,0,0,.3) 0px 2px 4px",
  cursor: "pointer",
  padding: `${ButtonSize["large"].padding}`,
  opacity: `${ButtonTheme["primary"]["light"]["enabled"].opacity}`,
  "&:hover": {
    background: `${ButtonTheme["primary"]["light"]["enabled"].hoverBackground}`,
    color: `${ButtonTheme["primary"]["light"]["enabled"].hoverColor}`,
    border: ` 1px solid ${ButtonTheme["primary"]["light"]["enabled"].hoverBorder}`,
  },
});

const InputField = {
  background: `${INPUT_BACKGROUND}`,
  borderRadius: "6px",
  fontFamily: "ArialMT",
  width: "calc(100% - 48px)",
  height: "36px",
  color: `${DARK_GREY}`,
  border: `1px solid ${PRIMARY_BLUE}`,
  fontSize: `16px`,
  boxShadow: "0px 0px 5px 0px rgba(15, 51, 95, 0.2)",
  margin: "0",
  letterSpacing: "0.32px",
  outline: "none",
  padding: "0",
  "&:hover": {
    background: `${INPUT_BACKGROUND}`,
    color: `${DARK_GREY}`,
    border: `${PRIMARY_BLUE}`,
  },
};

const themeChange = createTheme({
  palette: {
    primary: {
      // light: will be calculated from palette.primary.main,
      main: "#e6efff",
      // dark: will be calculated from palette.primary.main,
      // contrastText: will be calculated to contrast with palette.primary.main
    },
    text: {
      primary: "",
    },
  },
});

const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
  fontSize: "14px",
  [`& .${gridClasses.columnHeaders}`]: {
    backgroundColor: "#FFF",
  },
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: "none",
    "&:hover, &.Mui-hovered": {
      backgroundColor: themeChange.palette.primary.main,
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
  },
  [`& .${gridClasses.row}.odd`]: {
    fontSize: "14px",
    backgroundColor: "#FFF",
    "&:hover, &.Mui-hovered": {
      backgroundColor: themeChange.palette.primary.main,
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
  },
}));

export const EventMarkerTable: FC<EventMarkerTableProps> = (props) => {
  const {
    labels,
    rows,
    editItem,
    deleteItem,
    showMore,
    downloadAttachment,
    createEventMarker,
  } = props;
  const [loading, setLoading] = useState(false);
  const [sortedData, setSortedData] = useState([] as Array<EventMarkerItem>);
  const [gridData, setGridData] = useState<Array<EventMarkerItemGrid>>([]);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue] = useDebounce(searchValue, 500);

  useEffect(() => {
    if (debouncedSearchValue) {
      const filteredData: Array<EventMarkerItem> = rows.filter((row) => {
        return (
          row.eventMarkerDisplayDate
            .toLowerCase()
            .includes(debouncedSearchValue.toLowerCase()) ||
          row.eventMarkerDescription
            .toLowerCase()
            .includes(debouncedSearchValue.toLowerCase()) ||
          row.eventMarkerTypeTitle
            .toLowerCase()
            .includes(debouncedSearchValue.toLowerCase()) ||
          row.eventMarkerTypeAcronym
            .toLowerCase()
            .includes(debouncedSearchValue.toLowerCase()) ||
          row.eventMarkerDisplayDocuments
            .toLowerCase()
            .includes(debouncedSearchValue.toLowerCase()) ||
          row.eventMarkerDisplayTargets
            .toLowerCase()
            .includes(debouncedSearchValue.toLowerCase())
        );
      });
      formatGridData(filteredData);
    } else {
      formatGridData(rows);
    }
  }, [debouncedSearchValue]);

  useEffect(() => {
    setSortedData(rows);
    formatGridData(rows);
  }, [rows]);

  useEffect(() => {
    setLoading(true);
    formatGridData(rows);
  }, [rows]);

  const themeChange = createTheme({
    palette: {
      primary: {
        // light: will be calculated from palette.primary.main,
        main: "#e6efff",
        // dark: will be calculated from palette.primary.main,
        // contrastText: will be calculated to contrast with palette.primary.main
      },
      text: {
        primary: "",
      },
    },
  });

  function setEventMarker(eMarkerId: number) {
    if (eMarkerId >= 0) {
      let fdn = rows.filter((eml) => eml.eventMarkerId === eMarkerId);
      if (fdn.length > 0) {
        let typeInstances: Array<EventMarkerItem> = rows.filter(
          (evi) => evi.eventMarkerTypeAcronym === fdn[0].eventMarkerTypeAcronym
        );
        fdn[0].instancesOfType = typeInstances.length;
        editItem(fdn[0]);
      }
    }
  }

  function deleteEventMarker(eMarkerId: number) {
    if (eMarkerId > 0) {
      deleteItem(eMarkerId);
    }
  }

  function showMoreItems(itemList: string) {
    if (itemList.length > 0) {
      showMore(itemList);
    }
  }

  const customSort: GridComparatorFn<any>=(v1,v2)=>{
    const dateA = new Date(v1);
    const dateB = new Date(v2);

    if (dateA < dateB) {
      return 1;
    }

    if (dateA > dateB) {
      return -1;
    } 

    return 0;

  }

  const gridColumns: GridColDef[] = [
    {
      field: "eventMarkerDisplayDate",
      headerName: labels["EventDate.Text"] || "Event Date",
      type: "date",
      sortable: true,
      headerClassName: "evGridHeader",
      width: 120,
      sortComparator: customSort,
    },
    {
      field: "eventMarkerTypeTitle",
      headerName: labels["MarkerType.Text"] || "Event Type",
      editable: false,
      sortable: true,
      headerClassName: "evGridHeader",
      minWidth: 100,
      flex: 1,
      maxWidth: 130,
    },
    {
      field: "eventMarkerTypeColor",
      headerName: "",
      headerClassName: "evGridHeader",
      cellClassName: "evGridCellNoPadding",
      editable: false,
      sortable: false,
      width: 25,
      minWidth: 25,
      maxWidth: 25,
      align: "right",
      renderCell: (params: GridCellParams) => {
        const { value } = params;
        return <EventMarkerAcronym colorCode={value} />;
      },
    },
    {
      field: "eventMarkerTypeAcronym",
      headerName: labels["Label.Text"] || "Label",
      editable: false,
      sortable: true,
      headerClassName: "evGridHeaderPushLeft",
      width: 85,
      maxWidth: 85,
    },
    {
      field: "eventMarkerDisplayTargets",
      headerName: labels["BuildingsImpacted.Text"] || "Building(s) Impacted",
      editable: false,
      sortable: false,
      headerClassName: "evGridHeader",
      minWidth: 100,
      flex: 1,
    },  
    {
      field: "eventMarkerDescription",
      headerName: labels["Description.Text"] || "Description",
      editable: false,
      sortable: true,
      headerClassName: "evGridHeader",
      minWidth: 150,
    },
    {
      field: "eventMarkerDisplayDocuments",
      headerName: labels["Attachments.Text"] || "Attachments",
      editable: false,
      sortable: false,
      minWidth: 100,
      flex: 1,
      headerAlign: "right",
      align: "right",
      headerClassName: "evGridHeader",
      renderCell: (params: GridCellParams) => {
        const { value } = params;
        return (
          <EventMarkerDisplayDocs
            label={value}
            downloadRequest={downloadAttachment}
            showMore={showMoreItems}
          />
        );
      },
    },
    {
      field: "id",
      headerName: "",
      editable: false,
      sortable: false,
      headerClassName: "evGridHeader",
      minWidth: 50,
      maxWidth: 100,
      flex: 1,
      align: "right",
      renderCell: (params: GridCellParams) => {
        const { value } = params;
        return (
          <EditDeletIcons
            editEvent={setEventMarker}
            eMarkerId={value}
            deleteEvent={deleteEventMarker}
          />
        );
      },
    },
  ];

  const formatGridData = (rows: Array<EventMarkerItem>) => {
    let data: Array<EventMarkerItemGrid> = [];

    rows.forEach((element) => {
      data.push({
        id: element.eventMarkerId,
        eventMarkerDescription: element.eventMarkerDescription,
        eventMarkerDisplayDate: element.eventMarkerDisplayDate,
        eventMarkerTypeTitle: element.eventMarkerTypeTitle,
        eventMarkerTypeAcronym: element.eventMarkerTypeAcronym,
        eventMarkerTypeColor: element.eventMarkerTypeColor,
        eventMarkerDisplayTargets: element.eventMarkerDisplayTargets,
        eventMarkerDisplayDocuments: element.eventMarkerDisplayDocuments,
        acronymDisplay: {
          colorCode: element.eventMarkerTypeColor,
          label: element.eventMarkerTypeAcronym,
        },
      });
    });

    setGridData(data);
    setLoading(false);
  };

  const theme2 = createTheme({
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
          },
        },
      },
    },
  });

  return (
    <Grid
    id="mgmDashTop"
      container
      sx={{
        display: "flex",
        width: "calc(100% - 40px)",
        padding: "20px",
      }}
      spacing={2}
    >
      <Grid item xs={8}>
        <Box
          sx={{
            display: "flex",
            paddingTop: "10px",
          }}
          justifyContent="flex-start"
        >
          <Typography
            sx={{
              color: `${DARK_GREY}`,
              fontSize: "20px",
              fontWeight: "bold !important",
              fontFamily: "Lato,Helvetica Neue,Arial,Helvetica,sans-serif",
            }}
            variant="h1"
          >
            Event Marker
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={4}>
        <Box
          sx={{
            display: "flex",
          }}
          justifyContent="flex-end"
        >
          <ThemeProvider theme={theme2}>
            <ButtonStyles
              id="buttonEventMarkerCreate"
              variant="contained"
              onClick={(e) => {
                createEventMarker();
              }}
              startIcon={<AddIcon sx={{ marginRight: "0px !important" }} />}
            >
              Create Event Marker
            </ButtonStyles>
          </ThemeProvider>
        </Box>
      </Grid>
      <Grid item xs={4} justifyContent="start" sx={{ height: "auto" }}>
        <FormControl variant="standard">
          <OutlinedInput
          id="eventMarkerSearchInput"
            type="text"
            placeholder="Search"
            sx={InputField}
            onChange={(e) => {
              setSearchValue(e.target.value);
            }}
            endAdornment={
              <InputAdornment position="end">
                <SearchIcon sx={{ paddingRight: "5px" }} />
              </InputAdornment>
            }
            inputProps={{
              style: {
                border: "none",
                paddingLeft: "5px",
                fontFamily:'Arial'
              },
            }}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <>
          <StripedDataGrid
            sortingOrder={["asc", "desc"]}
            initialState={{
              sorting: {
                sortModel: [{ field: "eventMarkerDisplayDate", sort: "asc" }]
              }, // sort by eventMarkerDisplayDate                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                    
            }}
            sx={{
              ".MuiTablePagination-displayedRows": {
                "margin-top": "1em",
                "margin-bottom": "1em",
              },
              ".MuiTablePagination-selectLabel": {
                "margin-top": "1em",
                "margin-bottom": "1em",
              },
              '.MuiDataGrid-columnSeparator.MuiDataGrid-columnSeparator--sideRight': {
                display: 'none',
              },
            }}
            loading={loading}
            rowHeight={56}
            rows={gridData}
            columns={gridColumns}
            disableSelectionOnClick
            disableColumnFilter
            disableColumnMenu
            disableColumnSelector
            disableDensitySelector
            autoHeight={true}
            getRowClassName={(params) =>
              (params as any).indexRelativeToCurrentPage % 2 === 0
                ? "even"
                : "odd"
            }
            components={{ Pagination: CustomPagination }}
          />
        </>
      </Grid>
    </Grid>
  );
};

export default EventMarkerTable;
